import type { ReactNode } from 'react'
import type { ToolCall } from '../../../../types'
import { BUILTIN_TOOLS } from '../../../../integrations/utils'
import { StagedActionLoading } from '../../../../integrations/staged-action'
import { ReminderLoading } from '../../../../integrations/reminder'
import { ImageGenTool } from '../../../../integrations/backend-tools/imagegen'
import { SearchAudioNotesTool } from '../../../../integrations/backend-tools/audio-notes-loader'
import { SearchKnowledgeTool } from '../../../../integrations/backend-tools/knowledge-tool-loader'
import { SearchHighlightsTool } from '../../../../integrations/backend-tools/search-highlights-loader'
import { CustomInstructionsTool } from '../../../../integrations/custom-instructions/custom-instructions-tool'
import { ManageSkillTool } from '../../../../integrations/skills/manage-skill-tool'
import { AskUserQuestionCard, parseAskUserQuestionInput } from '../../../../integrations/ask-user-question'
import { AskUserFeedbackCard, parseAskUserFeedbackInput } from '../../../../integrations/ask-user-feedback'
import { parseAskUserSurfacedResult } from '@renderer/chat/integrations/ask-user-inline'
import {
  VIEW_FILES_TOOL_NAME,
  ViewedFilesTool,
  parseViewedFiles,
} from '@renderer/chat/integrations/files/viewed-files-tool'
import {
  OFFER_DOWNLOAD_TOOL_NAME,
  OfferedDownloadCard,
  parseOfferedDownload,
} from '@renderer/chat/integrations/files/offered-download'
import { parseToolCallResultCached } from './tool-call-utils'
import { extractNestedToolName } from './tool-view'

// ---------------------------------------------------------------------------
// Tools registry — the ONE place a tool name maps to its renderers.
//
// The main chat draws every tool call as the same row inside a collapsed
// group; of these renderers it reads only `card`, which `ToolCallList` shows
// in the thread instead of the row. `body` and `loading` are drawn by
// `ToolCallRenderer`, which renders a subagent's tool calls, and `submessage`
// by `ToolSubmessageRenderer` (a raw `role: 'tool'` submessage in the expanded
// group). Adding a tool type is one entry here plus its renderer file — never
// a new branch at a call site.
// ---------------------------------------------------------------------------

export type ToolBodyContext = {
  toolCall: ToolCall
  isLoading: boolean
  messageId: string
  conversationId: string
  isSubmessage?: boolean
}

export type ToolCardContext = {
  toolCall: ToolCall
  conversationId?: string
}

export type ToolSubmessageContext = {
  /** The tool message's string content, already known to be JSON-parseable. */
  result: any
}

export type ToolRegistryEntry = {
  /**
   * The tool call's body. Rendered whenever present; receives `isLoading` so
   * renderers with their own loading treatment stay one function.
   */
  body?: (ctx: ToolBodyContext) => ReactNode
  /**
   * Body shown only while the call is executing/ready. Tools with `loading`
   * but no `body` render nothing once complete (the result card takes over).
   */
  loading?: (ctx: ToolBodyContext) => ReactNode
  /** Rendering for a raw tool submessage carrying this tool's result. */
  submessage?: (ctx: ToolSubmessageContext) => ReactNode
  /**
   * A card the main chat shows in the thread in place of the tool's row, outside the collapsed tool group, for
   * something the user should see without opening it. Return null, as while the call runs, to keep the plain row.
   */
  card?: (ctx: ToolCardContext) => ReactNode
}

const toolRegistry: Record<string, ToolRegistryEntry> = {
  [BUILTIN_TOOLS.CREATE_STAGED_ACTION]: {
    loading: ({ toolCall }) => {
      const input = toolCall.toolInput as Record<string, unknown> | undefined
      const actionType = typeof input?.actionType === 'string' ? input.actionType : undefined
      return <StagedActionLoading actionType={actionType} />
    },
  },
  [BUILTIN_TOOLS.CREATE_REMINDER]: {
    loading: () => <ReminderLoading />,
  },
  [BUILTIN_TOOLS.GENERATE_IMAGE]: {
    body: ({ toolCall, isLoading }) => {
      const r = parseToolCallResultCached(toolCall)
      const input = toolCall.toolInput as { prompt: string }
      return (
        <ImageGenTool
          prompt={input.prompt}
          imageUrl={r?.url}
          error={r?.error || toolCall.error}
          isLoading={isLoading}
        />
      )
    },
  },
  [BUILTIN_TOOLS.SEARCH_AUDIO_NOTES]: {
    body: ({ toolCall, isLoading, isSubmessage }) => {
      const r = parseToolCallResultCached(toolCall)
      return (
        <SearchAudioNotesTool
          results={r?.results}
          isLoading={isLoading}
          error={toolCall.error}
          isSubmessage={isSubmessage}
        />
      )
    },
    submessage: ({ result }) => (
      <SearchAudioNotesTool results={result?.results} isLoading={false} error={result?.error} isSubmessage={true} />
    ),
  },
  [BUILTIN_TOOLS.SEARCH_KNOWLEDGE]: {
    body: ({ toolCall, isLoading, isSubmessage }) => {
      const r = parseToolCallResultCached(toolCall)
      return (
        <SearchKnowledgeTool results={r} isLoading={isLoading} error={toolCall.error} isSubmessage={isSubmessage} />
      )
    },
    submessage: ({ result }) => (
      <SearchKnowledgeTool results={result} isLoading={false} error={result?.error} isSubmessage={true} />
    ),
  },
  [BUILTIN_TOOLS.SEARCH_HIGHLIGHTS]: {
    body: ({ toolCall, isLoading, isSubmessage }) => {
      const r = parseToolCallResultCached(toolCall)
      return (
        <SearchHighlightsTool
          results={r?.results}
          isLoading={isLoading}
          error={toolCall.error}
          isSubmessage={isSubmessage}
        />
      )
    },
    submessage: ({ result }) => (
      <SearchHighlightsTool results={result?.results} isLoading={false} error={result?.error} isSubmessage={true} />
    ),
  },
  [BUILTIN_TOOLS.MANAGE_CUSTOM_INSTRUCTIONS]: {
    body: ({ toolCall, isLoading }) => {
      const r = parseToolCallResultCached(toolCall)
      return (
        <CustomInstructionsTool
          result={r}
          input={toolCall.toolInput as Record<string, string> | string | null}
          isLoading={isLoading}
          error={toolCall.error}
        />
      )
    },
  },
  [BUILTIN_TOOLS.MANAGE_SKILL]: {
    body: ({ toolCall, isLoading }) => (
      <ManageSkillTool
        result={toolCall.result || null}
        input={toolCall.toolInput as Record<string, unknown> | string | null}
        isLoading={isLoading}
        error={toolCall.error}
        messageId={toolCall.id}
      />
    ),
  },
  [BUILTIN_TOOLS.ASK_USER_QUESTION]: {
    // The agent ends the turn on this tool; the card posts the user's answer
    // back as a tool-result to resume. toolInput is stored parsed, but the
    // helper also defends against the string-input path.
    body: ({ toolCall, conversationId }) => {
      if (parseAskUserSurfacedResult(toolCall.result)) return null
      const parsedInput = parseAskUserQuestionInput(toolCall.toolInput)
      if (!parsedInput) return null
      return (
        <AskUserQuestionCard
          input={parsedInput}
          toolCallId={toolCall.id}
          conversationId={conversationId}
          existingResult={toolCall.result}
        />
      )
    },
  },
  [BUILTIN_TOOLS.ASK_USER_FEEDBACK]: {
    // Same turn-ending, client-resolved shape as AskUserQuestion — the card
    // posts the user's completions back as the tool-result.
    body: ({ toolCall, conversationId }) => {
      if (parseAskUserSurfacedResult(toolCall.result)) return null
      const parsedInput = parseAskUserFeedbackInput(toolCall.toolInput)
      if (!parsedInput) return null
      return (
        <AskUserFeedbackCard
          input={parsedInput}
          toolCallId={toolCall.id}
          conversationId={conversationId}
          existingResult={toolCall.result}
        />
      )
    },
  },
  [VIEW_FILES_TOOL_NAME]: {
    card: ({ toolCall }) => {
      const files = parseViewedFiles(parseToolCallResultCached(toolCall))
      return files.length > 0 ? <ViewedFilesTool files={files} /> : null
    },
  },
  [OFFER_DOWNLOAD_TOOL_NAME]: {
    card: ({ toolCall, conversationId }) => {
      const file = parseOfferedDownload(parseToolCallResultCached(toolCall))
      return file ? <OfferedDownloadCard file={file} conversationId={conversationId} /> : null
    },
  },
}

// Inner tools that resolve through the "other" wrapper (toolSet → other),
// by the name carried in `toolInput.name`.
const UNWRAPPABLE_OTHER_TOOLS = new Set<string>([
  BUILTIN_TOOLS.CREATE_REMINDER,
  VIEW_FILES_TOOL_NAME,
  OFFER_DOWNLOAD_TOOL_NAME,
])

/**
 * Resolve a tool call's registry entry. Tools wrapped in the "other" tool
 * pattern resolve by their inner name when the inner tool opted in.
 */
export function resolveToolEntry(toolCall: ToolCall): ToolRegistryEntry | undefined {
  const direct = toolRegistry[toolCall.toolName]
  if (direct) return direct
  if (toolCall.toolName === 'other') {
    const innerName = extractNestedToolName(toolCall)
    if (innerName && UNWRAPPABLE_OTHER_TOOLS.has(innerName)) return toolRegistry[innerName]
  }
  return undefined
}

/** The card a tool call shows in the thread instead of its row, or null to keep the row in the tool group. */
export function renderToolCard(toolCall: ToolCall, conversationId: string | undefined): ReactNode {
  if (toolCall.toolType === 'failure') return null
  return resolveToolEntry(toolCall)?.card?.({ toolCall, conversationId }) ?? null
}

/** Resolve a raw tool submessage's renderer by the message's tool name. */
export function resolveToolSubmessageEntry(name: string | undefined): ToolRegistryEntry | undefined {
  return name ? toolRegistry[name] : undefined
}
